import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ConfirmModal } from "../components/ConfirmModal";
import { Shell } from "../components/Shell";
import { ApiError, tenants } from "../lib/api";
import type { AIProviderID, PlatformConfig, ProviderStatus } from "../lib/api";

const PROVIDER_LABEL: Record<AIProviderID, string> = {
  anthropic: "Anthropic Claude",
  openai: "OpenAI ChatGPT",
  gemini: "Google Gemini",
  lm_studio: "LM Studio (local)",
  ollama: "Ollama (local)",
};

const PROVIDER_DESC: Record<AIProviderID, string> = {
  anthropic: "claude-haiku-4-5, claude-sonnet-4-6 · console.anthropic.com",
  openai: "gpt-4o-mini, gpt-4o, gpt-5 · platform.openai.com",
  gemini: "gemini-2.5-flash, gemini-2.5-pro · aistudio.google.com",
  lm_studio: "Endpoint local OpenAI-compatible · típico http://localhost:1234",
  ollama: "Endpoint local Ollama · típico http://localhost:11434",
};

const PROVIDER_KEY_URL: Record<AIProviderID, string | null> = {
  anthropic: "https://console.anthropic.com/settings/keys",
  openai: "https://platform.openai.com/api-keys",
  gemini: "https://aistudio.google.com/apikey",
  lm_studio: null,
  ollama: null,
};

export function PlatformConfigPage() {
  const [cfg, setCfg] = useState<PlatformConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const flash = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2800);
  };

  const reload = async () => {
    try {
      const c = await tenants.getPlatformConfig();
      setCfg(c);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo cargar");
    }
  };

  useEffect(() => {
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Shell>
        <section className="page">
          <div className="eyebrow">Cargando…</div>
        </section>
      </Shell>
    );
  }

  if (error || !cfg) {
    return (
      <Shell>
        <section className="page">
          <div className="alert err">{error ?? "No se pudo cargar"}</div>
          <p>
            <Link to="/tenants">← Volver</Link>
          </p>
        </section>
      </Shell>
    );
  }

  const cloud = cfg.providers.filter((p) => !p.requires_base_url);
  const local = cfg.providers.filter((p) => p.requires_base_url);
  const configuredCount = cfg.providers.filter((p) => p.configured).length;

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">Configuración plataforma</div>
        <h1 style={{ fontSize: 28, marginTop: 6 }}>Proveedores de IA</h1>
        <p className="lede" style={{ maxWidth: 720 }}>
          Configura credenciales de uno o más proveedores. Cada alcaldía elige
          cuál usar en su asistente. Los valores se cifran con KEK; cambios son
          inmediatos sin redeploy.
        </p>

        <div className="prov-summary">
          <span className="prov-summary-num">{configuredCount}</span>
          <span className="prov-summary-lbl">
            de {cfg.providers.length} proveedores configurados
          </span>
        </div>

        <h2 className="prov-section-title">Cloud</h2>
        <div className="prov-grid">
          {cloud.map((p) => (
            <ProviderCard
              key={p.provider}
              status={p}
              onChanged={() => void reload()}
              flash={flash}
            />
          ))}
        </div>

        <h2 className="prov-section-title" style={{ marginTop: 32 }}>
          Local (self-hosted)
        </h2>
        <div className="prov-grid">
          {local.map((p) => (
            <ProviderCard
              key={p.provider}
              status={p}
              onChanged={() => void reload()}
              flash={flash}
            />
          ))}
        </div>

        {toast && <div className={`toast ${toast.kind}`} role="status" aria-live="polite">{toast.msg}</div>}
      </section>
    </Shell>
  );
}

function ProviderCard({
  status,
  onChanged,
  flash,
}: {
  status: ProviderStatus;
  onChanged: () => void;
  flash: (kind: "ok" | "err", msg: string) => void;
}) {
  const [keyDraft, setKeyDraft] = useState("");
  const [urlDraft, setUrlDraft] = useState(status.base_url ?? "");
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const provider = status.provider;
  const isLocal = status.requires_base_url;
  const keyUrl = PROVIDER_KEY_URL[provider];

  const saveKey = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      await tenants.updateProviderConfig(provider, { api_key: keyDraft.trim() });
      onChanged();
      setKeyDraft("");
      flash("ok", "API key guardada");
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  const clearKey = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      await tenants.updateProviderConfig(provider, { api_key: "" });
      onChanged();
      flash("ok", "Credenciales borradas");
      setShowClearConfirm(false);
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  const saveUrl = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      await tenants.updateProviderConfig(provider, { base_url: urlDraft.trim() });
      onChanged();
      flash("ok", "Endpoint guardado");
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const r = await tenants.testProvider(provider);
      setTestResult({ ok: r.ok, detail: r.detail });
      flash(r.ok ? "ok" : "err", r.ok ? "Test OK" : "Test falló");
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "Test falló");
    } finally {
      setBusy(false);
    }
  };

  const placeholder =
    provider === "anthropic"
      ? "sk-ant-…"
      : provider === "openai"
        ? "sk-…"
        : provider === "gemini"
          ? "AIza…"
          : "(opcional)";

  return (
    <article className={`prov-card${status.configured ? " is-active" : ""}`}>
      <header className="prov-head">
        <div className="prov-title-block">
          <h3 className="prov-title">{PROVIDER_LABEL[provider]}</h3>
          <p className="prov-desc">{PROVIDER_DESC[provider]}</p>
        </div>
        <span className={`prov-pill${status.configured ? " is-ok" : ""}`}>
          <span className="dot" aria-hidden="true" />
          {status.configured ? "Activo" : "Sin configurar"}
          {status.api_key_fp && <span className="fp">····{status.api_key_fp}</span>}
        </span>
      </header>

      {status.has_env_fallback && (
        <div className="prov-env-warn">usando .env del servidor — recomendado migrar a DB</div>
      )}

      {isLocal && (
        <div className="prov-field">
          <label className="prov-label">Endpoint base URL</label>
          <div className="prov-row">
            <input
              type="text"
              className="input"
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              placeholder={
                provider === "lm_studio"
                  ? "http://host.docker.internal:1234"
                  : "http://host.docker.internal:11434"
              }
              disabled={busy}
            />
            <button
              type="button"
              className="cta ghost prov-btn"
              disabled={busy || urlDraft === (status.base_url ?? "")}
              onClick={() => void saveUrl()}
            >
              <span className="cta-inner">Guardar</span>
            </button>
          </div>
        </div>
      )}

      <div className="prov-field">
        <label className="prov-label">
          {isLocal ? "API key (opcional)" : status.configured ? "Rotar API key" : "API key"}
        </label>
        {keyUrl && (
          <p className="prov-hint">
            Obtén la key en{" "}
            <a href={keyUrl} target="_blank" rel="noreferrer">
              {keyUrl.replace(/^https?:\/\//, "")}
            </a>
          </p>
        )}
        <input
          type="password"
          autoComplete="off"
          className="input"
          value={keyDraft}
          onChange={(e) => setKeyDraft(e.target.value)}
          placeholder={placeholder}
          disabled={busy}
        />
        <div className="prov-actions">
          <button
            type="button"
            className="cta prov-btn"
            disabled={busy || keyDraft.trim().length < 8}
            onClick={() => void saveKey()}
          >
            <span className="cta-inner">
              {busy ? "Guardando…" : status.configured ? "Rotar" : "Guardar"}
            </span>
          </button>
          {status.configured && (
            <>
              <button
                type="button"
                className="btn-outline prov-btn"
                disabled={busy}
                onClick={() => void runTest()}
              >
                Probar
              </button>
              {status.api_key_fp && (
                <button
                  type="button"
                  className="btn-outline prov-btn is-danger"
                  disabled={busy}
                  onClick={() => setShowClearConfirm(true)}
                >
                  Borrar
                </button>
              )}
            </>
          )}
        </div>

        {testResult && (
          <div className={`prov-test ${testResult.ok ? "is-ok" : "is-err"}`}>
            <strong>{testResult.ok ? "✓ OK:" : "✗ Error:"}</strong> {testResult.detail}
          </div>
        )}
      </div>

      <ConfirmModal
        open={showClearConfirm}
        title={`¿Borrar credenciales de ${PROVIDER_LABEL[provider]}?`}
        message="La API key se borrará del servidor. Cualquier tenant que esté usando este proveedor dejará de funcionar hasta que vuelvas a configurarlo."
        confirmLabel="Sí, borrar"
        variant="danger"
        busy={busy}
        onCancel={() => setShowClearConfirm(false)}
        onConfirm={() => void clearKey()}
      />
    </article>
  );
}
